import React, { Dispatch, SetStateAction, useState } from "react";
import AdminModal from "../../../core/admin/adminModal";
import { IAd } from "./adminAds";
import AdImagesUploader from "./adImagesUploader";
import { deleteObject, getStorage, ref } from "firebase/storage";
import { db } from "../../../../firebase";
import { doc, Timestamp, updateDoc } from "firebase/firestore";
import { createDate, parseFirebaseTimestamp } from "../../../../utils/functions";
import AdminSwitch from "../../../core/admin/adminSwitch";
import AdminButton from "../../../core/admin/adminButton";
import AdminField from "../../../core/admin/adminField";
import AdminTextInput from "../../../core/admin/adminTextInput";
import AdminCallout from "../../../core/admin/adminCallout";
import AdminConfirm from "../../../core/admin/adminConfirm";
import AdminBadge from "../../../core/admin/adminBadge";
import { AdminLoading } from "../../../core/admin/adminSpinner";
import { PencilIcon, TrashIcon } from "../../../../img/svg/adminIcons";


export default function CompleteAdModal({ close, editableAd, setEditableAd, refresh }: { close: () => void, editableAd: IAd, setEditableAd: Dispatch<SetStateAction<IAd | null>>, refresh: () => void }) {

    const [loading, setLoading] = useState(false);
    const [deletion, setDeletion] = useState(false);
    const [mobileDeletion, setMobileDeletion] = useState(false);
    const [name, setName] = useState(editableAd?.name || "");
    const [link, setLink] = useState(editableAd?.link || "");
    const [until, setUntil] = useState(parseFirebaseTimestamp(editableAd?.until) || "");
    const [alter, setAlter] = useState(false);
    const [feedback, setFeedback] = useState("");

    const deleteImage = async (imageUrl: string, field: string) => {
        if (!editableAd?.id) {
            return null;
        }
        // setDeleteLoading(imageIdx);
        setLoading(true);
        const storage = getStorage();
        try {
            const imageRef = ref(storage, imageUrl);
            await deleteObject(imageRef);

        } catch (error) {
            console.log(error);
        }
        try {
            const docRef = doc(db, "ads", editableAd.id);
            let fields: { [key: string]: any } = {};
            fields[field] = "";
            fields["visible"] = false;
            await updateDoc(docRef, fields);
            refresh();
            setEditableAd({...editableAd, ...fields});
        } catch (error) {
            console.log(error);
        } finally {
            // antes o modal era remontado pelo recarregamento da lista, o que zerava estas confirmações
            setDeletion(false);
            setMobileDeletion(false);
            setLoading(false);
        }
    }

    const handleLink = (link: string) => {
        if(!link.includes("https://") && !link.includes("http://")){
            return "https://"+link;
        }
        return link;
    }

    const handleUpdate = async () => {
        try {
            if (name === editableAd.name && link === editableAd.link && until === editableAd?.until.toString()) {
                return null;
            }
            setLoading(true);
            const docRef = doc(db, "ads", editableAd?.id);
            await updateDoc(docRef, { link: handleLink(link), until: createDate(until), name });
            refresh();
            setEditableAd({...editableAd, link: handleLink(link), until: Timestamp.fromDate(createDate(until)), name});
            // mesmo resultado da antiga remontagem do modal: sai do modo de edição com o link já normalizado
            setLink(handleLink(link));
            setAlter(false);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

    const handleCancel = () => {
        setName(editableAd?.name || "");
        setLink(editableAd?.link || "");
        setUntil(parseFirebaseTimestamp(editableAd?.until) || "")
        setAlter(false);
    }

    const turnAdOn = async () => {
        try {
            setFeedback("");
            if (!editableAd.visible && (!editableAd.name || !editableAd.link || !editableAd.until || !editableAd.imgUrl || !editableAd.mImgUrl)) {
                setFeedback("Preencha todas as informações e envie as imagens para habilitar este anúncio");
                return null;
            }
            setLoading(true);
            const docRef = doc(db, "ads", editableAd?.id);
            await updateDoc(docRef, { visible: !editableAd.visible });
            refresh();
            setEditableAd({...editableAd, visible: !editableAd.visible});
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

    const finish = (fields: {}) => {
        setEditableAd({...editableAd, ...fields});
    }

    const expired = editableAd?.until && editableAd.until < Timestamp.now();

    return (
        <AdminModal close={close} size="lg" title={editableAd?.name || "Detalhes do anúncio"} subtitle="Detalhes do anúncio">
            <div className="flex flex-col gap-6">
                {/* VISIBILIDADE */}
                <section className="rounded-xl border border-gray-200 p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                            <h3 className="font-bold text-gray-900 flex flex-wrap items-center gap-2">
                                Visibilidade
                                {
                                    expired ?
                                        <AdminBadge tone="danger">Data final já passou</AdminBadge>
                                        : null
                                }
                            </h3>
                            <p className="text-sm text-gray-500 mt-0.5">O anúncio só ficará visível enquanto o botão estiver ligado e a data final não tiver passado.</p>
                        </div>
                        <AdminSwitch on={editableAd.visible} disabled={loading} onClick={turnAdOn} label={editableAd.visible ? "Visível" : "Oculto"} />
                    </div>
                    {
                        feedback ?
                            <AdminCallout tone="danger" className="mt-3">{feedback}</AdminCallout>
                            : null
                    }
                </section>

                {/* INFORMAÇÕES */}
                <section className="rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between gap-3 mb-4">
                        <h3 className="font-bold text-gray-900">Informações</h3>
                        {
                            !alter && !loading ?
                                <AdminButton size="sm" variant="secondary" icon={<PencilIcon width="14px" />} onClick={() => setAlter(true)}>Alterar</AdminButton>
                                : null
                        }
                    </div>
                    {
                        loading ?
                            <AdminLoading />
                            :
                            <div className="flex flex-col gap-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <AdminField label="Nome" htmlFor="ad-name">
                                        <AdminTextInput id="ad-name" disabled={!alter} value={name} onChange={e => setName(e.target.value)} placeholder="Nome" />
                                    </AdminField>
                                    <AdminField label="Data final" htmlFor="ad-until" hint="Formato dd/mm/aaaa">
                                        <AdminTextInput id="ad-until" inputMode="numeric" disabled={!alter} value={until} onChange={e => setUntil(e.target.value)} placeholder="Data de expiração" />
                                    </AdminField>
                                </div>
                                <AdminField label="Link" htmlFor="ad-link">
                                    <AdminTextInput id="ad-link" inputMode="url" disabled={!alter} value={link} onChange={e => setLink(e.target.value)} placeholder="Link" />
                                </AdminField>
                                {
                                    alter ?
                                        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                                            <AdminButton variant="secondary" onClick={handleCancel}>Cancelar</AdminButton>
                                            <AdminButton onClick={handleUpdate}>Salvar</AdminButton>
                                        </div>
                                        : null
                                }
                            </div>
                    }
                </section>

                {/* IMAGENS */}
                <section className="flex flex-col gap-4">
                    <h3 className="font-bold text-gray-900">Imagens</h3>
                    <AdminCallout tone="warning" title="Atenção!">
                        <ul className="list-disc list-inside">
                            <li>As imagens devem ter 132px de altura;</li>
                            <li>A imagem desktop deve ter no mínimo 768px de largura;</li>
                            <li>A imagem mobile deve ter no máximo 768px de largura;</li>
                            <li>O conteúdo das imagens deve estar centralizado.</li>
                        </ul>
                    </AdminCallout>

                    <AdImageSlot
                        label="Imagem Desktop"
                        loading={loading}
                        imageUrl={editableAd?.imgUrl}
                        deletion={deletion}
                        setDeletion={setDeletion}
                        onDelete={() => deleteImage(editableAd?.imgUrl, "imgUrl")}
                        uploader={<AdImagesUploader ad={editableAd} update={finish} refresh={refresh} />}
                    />
                    <AdImageSlot
                        label="Imagem Mobile"
                        loading={loading}
                        imageUrl={editableAd?.mImgUrl}
                        deletion={mobileDeletion}
                        setDeletion={setMobileDeletion}
                        onDelete={() => deleteImage(editableAd?.mImgUrl, "mImgUrl")}
                        uploader={<AdImagesUploader ad={editableAd} update={finish} refresh={refresh} mobile />}
                    />
                </section>
            </div>
        </AdminModal>
    )
}

// área de uma das imagens do anúncio: prévia com exclusão (com confirmação) ou o uploader
function AdImageSlot({ label, loading, imageUrl, deletion, setDeletion, onDelete, uploader }: { label: string, loading: boolean, imageUrl: string, deletion: boolean, setDeletion: (d: boolean) => void, onDelete: () => void, uploader: React.ReactNode }) {
    return (
        <div className="rounded-xl border border-gray-200 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    {label}
                    {
                        imageUrl ?
                            <AdminBadge tone="success" dot>Enviada</AdminBadge>
                            : <AdminBadge tone="warning" dot>Pendente</AdminBadge>
                    }
                </p>
                {
                    imageUrl && !loading ?
                        deletion ?
                            <AdminConfirm message="Excluir imagem?" confirmLabel="Excluir" cancelLabel="Cancelar" danger onConfirm={onDelete} onCancel={() => setDeletion(false)} />
                            : <AdminButton size="sm" variant="ghost" className="!text-red-600 hover:!bg-red-50" icon={<TrashIcon width="14px" />} onClick={() => setDeletion(true)}>Excluir</AdminButton>
                        : null
                }
            </div>
            {
                loading ?
                    <AdminLoading />
                    : imageUrl ?
                        <div className="relative w-full h-[132px] overflow-hidden flex items-center rounded-lg bg-gray-950">
                            <img src={imageUrl} alt={label} className="mx-auto" />
                        </div>
                        : uploader
            }
        </div>
    )
}
